import { useState, useEffect, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { IntlProvider } from "react-intl-next";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ProgressBar from "@atlaskit/progress-bar";
import { token } from "@atlaskit/tokens";

import { useAnalyticsClient } from "@/analytics/wac-analytics";

import { StepContext, StepProps } from "../../../types";
import { Header } from "../../components/header";

import { stepMessages } from "./messages";

export type MadlibStepContext = {
    data?: {
        task: string;
        goal: string;
        dueDate: number;
    };
} & StepContext;

/**
 * This Loading component is designed to execute an asynchronous action (like space setup or invite) when the step loads, and display a progress bar while waiting for the action to complete.
 * It also simulates progressive loading by incrementally updating the progress bar value until the action is complete.
 * @param param0
 * @returns
 */
export const Loading = <T extends StepContext>({
    context,
    setContext,
    nextStep,
    onLoad
}: StepProps<T>) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [header, setHeader] = useState(
        intl.formatMessage(stepMessages.creationInProgressHeader)
    );
    const [subheader, setSubheader] = useState(
        intl.formatMessage(stepMessages.creationInProgressSubheader)
    );

    const [spaceSetupComplete, setspaceSetupComplete] = useState(false);
    const [progressValue, setProgressValue] = useState(0);
    const analyticsClient = useAnalyticsClient();
    const onLoadTriggered = useRef(false);

    useEffect(() => {
        if (onLoad && !onLoadTriggered.current) {
            onLoadTriggered.current = true;
            onLoad({ context, setContext }).finally(() =>
                setspaceSetupComplete(true)
            );
        }
    }, [onLoad, context, setContext]);

    // Progressive progress bar simulation - reaches 97% in 17 seconds
    useEffect(() => {
        if (spaceSetupComplete) {
            return;
        }

        const startTime = Date.now();
        const targetDuration = 17000; // 17 seconds
        const startProgress = 1; // Starting at 1%
        const targetProgress = 97; // Target 97%
        const progressRange = targetProgress - startProgress; // 96%

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const timeProgress = Math.min(elapsed / targetDuration, 1);
            const newValue = startProgress + timeProgress * progressRange;

            setProgressValue(Math.min(newValue, targetProgress));
        }, 100); // Update every 100ms for smoother animation

        return () => clearInterval(interval);
    }, [spaceSetupComplete]);

    useEffect(() => {
        createAnalyticsEvent({
            action: "viewed",
            actionSubject: "screen",
            actionSubjectId: "spaceSetupInProgress",
            eventType: "screen",
            name: "spaceSetupInProgress",
            attributes: {
                teamType: context.teamType || "unknown"
            }
        }).fire();
    }, [intl, analyticsClient, createAnalyticsEvent, context.teamType]);

    // update header and subheader when space setup is complete and move to next step
    useEffect(() => {
        if (spaceSetupComplete) {
            setProgressValue(100);
            setHeader(intl.formatMessage(stepMessages.creationCompleteHeader));
            setSubheader("");

            if (analyticsClient) {
                createAnalyticsEvent({
                    action: "viewed",
                    actionSubject: "screen",
                    actionSubjectId: "spaceSetupComplete",
                    eventType: "screen",
                    name: "spaceSetupComplete",
                    attributes: {
                        teamType: context.teamType || "unknown"
                    }
                }).fire();
            }

            // move to next step after 2 seconds
            const nextStepTimeout = setTimeout(() => {
                nextStep();
            }, 2_000);

            return () => clearTimeout(nextStepTimeout);
        }
    }, [
        spaceSetupComplete,
        intl,
        nextStep,
        analyticsClient,
        createAnalyticsEvent,
        context.teamType
    ]);

    return (
        <IntlProvider locale={intl.locale}>
            <div css={container}>
                <Header header={header} subheader={subheader} />
                <div
                    css={[
                        progressBarContainer,
                        progressValue >= 97 &&
                            !spaceSetupComplete &&
                            progressBarPulsing
                    ]}
                    style={
                        {
                            "--ds-background-neutral-bold": token(
                                "color.border.accent.blue"
                            )
                        } as React.CSSProperties
                    }
                >
                    <ProgressBar
                        data-testid="progress-bar"
                        value={progressValue / 100}
                        appearance={spaceSetupComplete ? "success" : undefined}
                    />
                </div>
            </div>
        </IntlProvider>
    );
};

const container = css({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
});

const progressBarContainer = css({
    flex: 1
});

const progressBarPulsing = css({
    "animation": "progressPulse 1.5s ease-in-out infinite",
    "@keyframes progressPulse": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Required for keyframe animation
        "0%": {
            opacity: 1
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Required for keyframe animation
        "50%": {
            opacity: 0.7
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Required for keyframe animation
        "100%": {
            opacity: 1
        }
    }
});
